
/*
  UXS — VOICE002 V6
  LITTLE SIGNAL / SOFT MUSICAL VOCODER

  DAY2K — UNIDOS X EL SONIDO

  IDENTIDAD:
  - Robotito cálido y musical
  - Ligeramente distinto de VOICE001
  - Vocoder de 20 bandas
  - Acorde Gmaj9
  - Voz parcialmente comprensible
  - Menos aspereza y saturación
  - Volumen estable durante la frase
  - Tres notas suaves de ear candy

  Conserva:
  UXSVocoder.process(blob)
  UXSVocoder.toWav(buffer)

  Procesamiento local.
*/

(function () {
  "use strict";

  const S = {
    bands: 20,
    low: 125,
    high: 4600,

    sensitivity: 7.2,
    dry: 0.055,
    output: 0.76,

    tail: 1.5,

    // Nivelación moderada.
    maxRide: 2.4,
    rideTarget: 0.072
  };

  const clamp = (v, a, b) =>
    Math.max(a, Math.min(b, v));

  const chord = [
    // Gmaj9 con un centro más cálido.
    [146.83, 0.35, "triangle"],  // D3
    [196.00, 0.55, "triangle"],  // G3
    [246.94, 0.42, "sawtooth"], // B3
    [293.66, 0.48, "triangle"], // D4
    [369.99, 0.31, "triangle"], // F#4
    [440.00, 0.20, "triangle"]  // A4
  ];

  // =====================================
  // FILTRO
  // =====================================

  function filter(ctx, type, frequency, Q = 0.707) {
    const node = ctx.createBiquadFilter();

    node.type = type;
    node.frequency.value = frequency;
    node.Q.value = Q;

    return node;
  }

  // =====================================
  // SINTETIZADOR
  // =====================================

  function buildCarrier(ctx, duration) {
    const bus = ctx.createGain();
    bus.gain.value = 0.16;

    chord.forEach(([hz, level, shape], i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = shape;
      osc.frequency.value = hz;

      // Pequeñas diferencias armónicas.
      osc.detune.value =
        i % 2 === 0 ? -1.7 : 1.7;

      gain.gain.value = level;

      osc.connect(gain);
      gain.connect(bus);

      osc.start(0);
      osc.stop(duration);
    });

    // Suavizar armónicos agresivos.
    const lowpass = filter(
      ctx,
      "lowpass",
      4400,
      0.55
    );

    bus.connect(lowpass);

    return lowpass;
  }

  // =====================================
  // SALIDA
  // =====================================

  function buildOutput(ctx) {
    const mix = ctx.createGain();
    mix.gain.value = S.output;

    const warmth = filter(
      ctx,
      "peaking",
      340,
      0.9
    );

    warmth.gain.value = 2.2;

    const presence = filter(
      ctx,
      "peaking",
      1450,
      0.9
    );

    presence.gain.value = 1.2;

    const harshness = filter(
      ctx,
      "peaking",
      3300,
      1.1
    );

    harshness.gain.value = -3.7;

    const top = filter(
      ctx,
      "lowpass",
      5300,
      0.55
    );

    const compressor =
      ctx.createDynamicsCompressor();

    compressor.threshold.value = -26;
    compressor.knee.value = 18;
    compressor.ratio.value = 2.5;
    compressor.attack.value = 0.012;
    compressor.release.value = 0.22;

    mix.connect(warmth);
    warmth.connect(presence);
    presence.connect(harshness);
    harshness.connect(top);
    top.connect(compressor);
    compressor.connect(ctx.destination);

    return mix;
  }

  // =====================================
  // MELODÍA DE DESPEDIDA
  // =====================================

  function addEarCandy(ctx, output, startAt) {
    const bus = ctx.createGain();
    bus.gain.value = 0.24;

    const soft = filter(
      ctx,
      "lowpass",
      2600,
      0.6
    );

    bus.connect(soft);
    soft.connect(output);

    // B4 → A4 → F#4
    const notes = [
      [493.88, 0.00],
      [440.00, 0.27],
      [369.99, 0.56]
    ];

    notes.forEach(([hz, delay], index) => {
      const start = startAt + 0.08 + delay;
      const end = start + 0.57;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "triangle";
      osc.frequency.value = hz;

      gain.gain.setValueAtTime(
        0.0001,
        start
      );

      gain.gain.linearRampToValueAtTime(
        index === 2 ? 0.047 : 0.060,
        start + 0.045
      );

      gain.gain.exponentialRampToValueAtTime(
        0.0001,
        end
      );

      osc.connect(gain);
      gain.connect(bus);

      osc.start(start);
      osc.stop(end + 0.01);
    });
  }

  // =====================================
  // NIVELACIÓN SUAVE DE VOZ
  // =====================================

  function stabilize(buffer, voiceDuration) {
    const data = buffer.getChannelData(0);
    const rate = buffer.sampleRate;

    // Medimos energía en ventanas de 60 ms.
    const windowSize = Math.max(
      128,
      Math.floor(rate * 0.06)
    );

    let previousGain = 1;

    for (
      let start = 0;
      start < data.length;
      start += windowSize
    ) {
      const end = Math.min(
        start + windowSize,
        data.length
      );

      const time = start / rate;

      // No alterar las tres notas finales.
      if (time >= voiceDuration) {
        continue;
      }

      let sum = 0;

      for (let i = start; i < end; i++) {
        sum += data[i] * data[i];
      }

      const rms = Math.sqrt(
        sum / Math.max(1, end - start)
      );

      // No elevar demasiado el silencio
      // ni los ruidos del micrófono.
      const targetGain =
        rms < 0.009
          ? 1
          : clamp(
              S.rideTarget / Math.max(rms, 0.001),
              0.8,
              S.maxRide
            );

      // Cambios lentos para evitar bombeo.
      const smoothedGain =
        previousGain +
        (targetGain - previousGain) * 0.16;

      for (let i = start; i < end; i++) {
        const progress =
          (i - start) / Math.max(1, end - start);

        const gain =
          previousGain +
          (smoothedGain - previousGain) * progress;

        data[i] *= gain;
      }

      previousGain = smoothedGain;
    }

    // Evitar clipping.
    let peak = 0;

    for (let i = 0; i < data.length; i++) {
      peak = Math.max(
        peak,
        Math.abs(data[i])
      );
    }

    if (peak > 0.94) {
      const factor = 0.94 / peak;

      for (let i = 0; i < data.length; i++) {
        data[i] *= factor;
      }
    }

    return buffer;
  }

  // =====================================
  // PROCESAR
  // =====================================

  async function process(blob) {
    const AC =
      window.AudioContext ||
      window.webkitAudioContext;

    const OAC =
      window.OfflineAudioContext ||
      window.webkitOfflineAudioContext;

    if (!AC || !OAC) {
      throw new Error(
        "Web Audio no disponible"
      );
    }

    const decoder = new AC();

    let input;

    try {
      input = await decoder.decodeAudioData(
        await blob.arrayBuffer()
      );
    } finally {
      await decoder.close();
    }

    if (
      !input.duration ||
      input.duration > 90
    ) {
      throw new Error(
        "Duración inválida"
      );
    }

    const rate = input.sampleRate;
    const duration = input.duration + S.tail;

    const ctx = new OAC(
      1,
      Math.ceil(duration * rate),
      rate
    );

    const speech = ctx.createBufferSource();
    speech.buffer = input;

    // ===================================
    // PREPROCESAMIENTO
    // ===================================

    const highpass = filter(
      ctx,
      "highpass",
      100
    );

    const preComp =
      ctx.createDynamicsCompressor();

    preComp.threshold.value = -30;
    preComp.knee.value = 18;
    preComp.ratio.value = 2.7;
    preComp.attack.value = 0.008;
    preComp.release.value = 0.20;

    const inputGain = ctx.createGain();
    inputGain.gain.value = 1.6;

    speech.connect(highpass);
    highpass.connect(preComp);
    preComp.connect(inputGain);

    const carrier = buildCarrier(
      ctx,
      duration
    );

    const output = buildOutput(ctx);

    // ===================================
    // RECTIFICADOR
    // ===================================

    const curve = new Float32Array(2049);

    for (let i = 0; i < curve.length; i++) {
      const x =
        i / (curve.length - 1) * 2 - 1;

      curve[i] = Math.abs(x);
    }

    // ===================================
    // VOCODER DE 20 BANDAS
    // ===================================

    const high = Math.min(
      S.high,
      rate * 0.42
    );

    for (let i = 0; i < S.bands; i++) {
      const hz =
        S.low *
        Math.pow(
          high / S.low,
          i / (S.bands - 1)
        );

      // Análisis vocal.
      const analysis = filter(
        ctx,
        "bandpass",
        hz,
        2.1
      );

      const rectifier = ctx.createWaveShaper();
      rectifier.curve = curve;
      rectifier.oversample = "none";

      const envelope = filter(
        ctx,
        "lowpass",
        hz < 800 ? 38 :
        hz < 2500 ? 48 : 56
      );

      const sensitivity = ctx.createGain();
      sensitivity.gain.value = S.sensitivity;

      inputGain.connect(analysis);
      analysis.connect(rectifier);
      rectifier.connect(envelope);
      envelope.connect(sensitivity);

      // Síntesis musical.
      const synthesis = filter(
        ctx,
        "bandpass",
        hz,
        1.85
      );

      const modulated = ctx.createGain();
      modulated.gain.value = 0;

      carrier.connect(synthesis);
      synthesis.connect(modulated);
      sensitivity.connect(modulated.gain);

      const bandLevel = ctx.createGain();

      bandLevel.gain.value =
        hz < 850 ? 1.14 :
        hz < 2600 ? 1.02 :
        0.58;

      modulated.connect(bandLevel);
      bandLevel.connect(output);
    }

    // ===================================
    // ARTICULACIÓN PARCIAL
    // ===================================

    // Pequeña cantidad de voz natural:
    // el vocoder sigue siendo protagonista.

    const voiceHP = filter(
      ctx,
      "highpass",
      750
    );

    const voiceLP = filter(
      ctx,
      "lowpass",
      2700
    );

    const definition = ctx.createGain();
    definition.gain.value = S.dry;

    inputGain.connect(voiceHP);
    voiceHP.connect(voiceLP);
    voiceLP.connect(definition);
    definition.connect(output);

    // ===================================
    // EAR CANDY
    // ===================================

    addEarCandy(
      ctx,
      output,
      input.duration
    );

    speech.start(0);

    const rendered =
      await ctx.startRendering();

    return stabilize(
      rendered,
      input.duration
    );
  }

  // =====================================
  // EXPORTAR WAV
  // =====================================

  function toWav(buffer) {
    const samples = buffer.getChannelData(0);

    const bytes = new ArrayBuffer(
      44 + samples.length * 2
    );

    const view = new DataView(bytes);

    function tag(pos, text) {
      for (let i = 0; i < text.length; i++) {
        view.setUint8(
          pos + i,
          text.charCodeAt(i)
        );
      }
    }

    tag(0, "RIFF");

    view.setUint32(
      4,
      bytes.byteLength - 8,
      true
    );

    tag(8, "WAVE");
    tag(12, "fmt ");

    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);

    view.setUint32(
      24,
      buffer.sampleRate,
      true
    );

    view.setUint32(
      28,
      buffer.sampleRate * 2,
      true
    );

    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);

    tag(36, "data");

    view.setUint32(
      40,
      samples.length * 2,
      true
    );

    for (let i = 0; i < samples.length; i++) {
      const s = clamp(
        samples[i],
        -1,
        1
      );

      view.setInt16(
        44 + i * 2,
        Math.round(
          s * (s < 0 ? 32768 : 32767)
        ),
        true
      );
    }

    return new Blob(
      [bytes],
      { type: "audio/wav" }
    );
  }

  window.UXSVocoder = {
    process,
    toWav
  };

  console.log(
    "UXS VOICE002 V6 — Little Signal Ready"
  );
})();
